import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Package, Plus, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState, TableSkeleton } from "@/components/ui/states";
import { fetchProducts, type CatalogFilter } from "./api";

const PAGE_SIZE = 25;

const FILTERS: { id: CatalogFilter; label: string }[] = [
  { id: "all", label: "Todos" },
  { id: "active", label: "Publicados" },
  { id: "inactive", label: "Ocultos" },
  { id: "featured", label: "Destacados" },
  { id: "archived", label: "Archivados" },
];

export function ProductsPage() {
  const { org } = useActiveOrg();
  const [filter, setFilter] = useState<CatalogFilter>("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => setPage(0), [filter, debounced, org?.organizationId]);

  const query = useQuery({
    queryKey: ["products", org?.organizationId, filter, debounced, page],
    queryFn: () =>
      fetchProducts({
        organizationId: org!.organizationId,
        filter,
        search: debounced,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: Boolean(org),
    placeholderData: keepPreviousData,
  });

  const currency = org?.currency ?? "PEN";

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-head font-semibold tracking-tight">Catálogo</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nombre del producto"
              aria-label="Buscar productos"
              className="w-full rounded-md border border-line bg-surface py-1.5 pl-8 pr-3 text-ui sm:w-64"
            />
          </div>
          {/* Link y no Button: es navegacion, y asi sigue funcionando abrir
              en pestana nueva. Button renderiza un <button>, que no navega. */}
          <Link
            to="/catalogo/nuevo"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent bg-accent px-2.5 py-1.5 text-meta font-medium leading-tight text-accent-ink transition-colors hover:opacity-90"
          >
            <Plus className="size-3.5" />
            Nuevo producto
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              filter === f.id
                ? "border-ink bg-ink text-ground"
                : "border-line bg-surface text-ink-2 hover:bg-sunk",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Panel bleed>
        <QueryBoundary
          query={query}
          skeleton={<TableSkeleton rows={8} cols={5} />}
          isEmpty={(d) => d.rows.length === 0}
          empty={
            <EmptyState
              icon={<Package className="size-5" />}
              title={debounced || filter !== "all" ? "Ningún producto coincide" : "Aún no tienes productos"}
              description={
                debounced || filter !== "all"
                  ? "Prueba con otro filtro o quita la búsqueda."
                  : "Crea el primero para que aparezca en tu tienda."
              }
            />
          }
        >
          {(data) => (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse text-ui">
                  <thead>
                    <tr>
                      {["Producto", "Categoría", "Variantes", "Precio", "Estado"].map((h, i) => (
                        <th
                          key={h}
                          className={cn(
                            "border-b border-line bg-sunk px-4 py-2.5 font-mono text-mark font-medium uppercase tracking-[0.1em] text-ink-3",
                            i === 2 || i === 3 ? "text-right" : "text-left",
                          )}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((p) => (
                      <tr key={p.id} className="border-b border-line-2 last:border-0">
                        <td className="px-4 py-3">
                          <Link to={`/catalogo/${p.id}`} className="font-medium hover:text-accent">
                            {p.name}
                          </Link>
                          <span className="block text-xs text-ink-3">
                            {p.brandName ?? "Sin marca"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-ink-2">{p.categoryName ?? "—"}</td>
                        <td className="tnum px-4 py-3 text-right text-ink-2">{p.variantCount}</td>
                        <td className="tnum px-4 py-3 text-right font-semibold">
                          {p.minPrice === null
                            ? "—"
                            : p.minPrice === p.maxPrice
                              ? formatMoney(p.minPrice, currency, { withSymbol: false })
                              : `${formatMoney(p.minPrice, currency, { withSymbol: false })} – ${formatMoney(p.maxPrice, currency, { withSymbol: false })}`}
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex gap-1.5">
                            <StatusPill tone={p.active ? "ok" : "neutral"}>
                              {p.active ? "Publicado" : "Oculto"}
                            </StatusPill>
                            {p.featured && (
                              <StatusPill tone="accent" dot={false}>
                                Destacado
                              </StatusPill>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-line-2 px-4 py-2.5">
                <span className="tnum text-xs text-ink-3">{data.total} productos</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={(page + 1) * PAGE_SIZE >= data.total}
                    onClick={() => setPage(page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </QueryBoundary>
      </Panel>
    </div>
  );
}
